import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, computed, inject, type OnInit } from '@angular/core';

import type { LogoEntity, SelectedFileLegacy } from '@sites/entity-core';
import { logoMaxWidthCss } from '@sites/entity-core/src/widget/logo/styles/logo.css-vars';
import type { LogoForm } from '@sites/entity-signal-form';

import { formatFileUrl, formatFileUrlWithResize } from '@components/sp-file-manager/src/helpers';
import { SpDirectivesModule } from '@common/directives/directives.module';
import { environment } from '@web-env/environment';

import { BLANK_LOGO } from '@web/widgets/image/constants/image.consts';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';

/**
 * Editor preview for the `logo` widget — signal-based. The form is shared with
 * the control panel via `WebLegacyWidgetSignalFormService` (ref-counted by widget id).
 *
 * The widget is a picked file in a width-capped box; the cap is the only dynamic
 * style, and it lives in the entity's `styles`, not in `data`.
 */
@Component({
    selector: 'web-logo-view',
    templateUrl: './logo-view.component.html',
    styleUrls: ['./logo-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpDirectivesModule],
})
export class LogoViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<LogoEntity['data']>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);

    public form!: LogoForm;

    public readonly image = computed<SelectedFileLegacy | null>(() => this.form.image().value());

    /** Box width cap (CSS custom property bound on the box). */
    public readonly maxWidth = computed<string | null>(() => logoMaxWidthCss(this.form.maxWidth().value()));

    /** Resized delivery URL, with the full-size original as the fallback. */
    public readonly imgSrc = computed<string>(() => {
        const image = this.image();
        return image ? formatFileUrlWithResize(image, environment.fileManagerCdnUrl, 720) : BLANK_LOGO;
    });

    public readonly imgFallbackSrc = computed<string>(() => {
        const image = this.image();
        return image ? formatFileUrl(image, environment.fileManagerCdnUrl) : BLANK_LOGO;
    });

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<LogoForm>(this.widget().id, this.destroyRef);
    }
}
